/**
 * Utility functions for handling location data
 */

/**
 * Safely render location data from various formats
 * @param location - Location data that can be string, object, or undefined
 * @returns A string representation of the location
 */
export const renderLocation = (location: any): string => {
  if (!location) return 'Unknown';
  
  // If it's already a string, return it
  if (typeof location === 'string') return location;
  
  // If it's an object, try to extract meaningful location info
  if (typeof location === 'object') {
    // Check for common location properties
    const area = location.area || location.area_name;
    const camera = location.camera || location.camera_name;
    const zone = location.zone_name || location.zone;
    const name = location.name;
    
    // Return the first available property
    return area || camera || zone || name || 'Unknown';
  }
  
  return 'Unknown';
};

/**
 * Format location with additional details
 * @param location - Location data
 * @param includeDetails - Whether to include position data
 * @returns Formatted location string
 */
export const formatLocationWithDetails = (location: any, includeDetails = false): string => {
  const basic = renderLocation(location);
  
  if (!includeDetails || !location || typeof location !== 'object') {
    return basic;
  }
  
  // Add position data if available
  if (location.position_data) {
    const { x, y } = location.position_data;
    if (x !== undefined && y !== undefined) {
      return `${basic} (${Math.round(x)}, ${Math.round(y)})`;
    }
  }
  
  return basic;
};

/**
 * Check if location data is valid
 * @param location - Location data to validate
 * @returns Boolean indicating if location is valid
 */
export const isValidLocation = (location: any): boolean => {
  if (!location) return false;
  if (typeof location === 'string') return location.trim().length > 0;
  if (typeof location === 'object') {
    return !!(location.area || location.camera || location.zone_name || location.name);
  }
  return false;
};